import { createElement } from 'react';
import type { MenuProps } from 'antd';
import type { CurrentUser } from '../services/authService';
import {
  NAV_ADMIN_SUBMENU_ID,
  type NavLink,
  type NavNode,
  type NavSubmenu,
} from '../config/nav';
import { PermissionUtils } from './permissions';

export function canAccessNavLink(user: CurrentUser | null, link: NavLink): boolean {
  const access = link.access ?? 'all';
  if (access === 'superAdmin') return PermissionUtils.isSuperAdmin(user);
  if (access === 'admin') return PermissionUtils.isAdmin(user);
  return true;
}

export function filterNav(nodes: NavNode[], user: CurrentUser | null): NavNode[] {
  const result: NavNode[] = [];

  for (const node of nodes) {
    if (node.kind === 'link') {
      if (canAccessNavLink(user, node)) result.push(node);
      continue;
    }

    if (node.kind === 'group') {
      const children = node.children.filter((child) => canAccessNavLink(user, child));
      if (children.length > 0) {
        result.push({ ...node, children });
      }
      continue;
    }

    const access = node.access ?? 'all';
    if (access === 'superAdmin' && !PermissionUtils.isSuperAdmin(user)) continue;
    if (access === 'admin' && !PermissionUtils.isAdmin(user)) continue;

    const children = node.children.filter((child) => canAccessNavLink(user, child));
    if (children.length > 0) {
      result.push({ ...node, children });
    }
  }

  return result;
}

export function collectLinks(nodes: NavNode[]): NavLink[] {
  const links: NavLink[] = [];
  for (const node of nodes) {
    if (node.kind === 'link') {
      links.push(node);
    } else {
      links.push(...node.children);
    }
  }
  return links;
}

function pathMatches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Longest prefix wins so /ai-access/zhipuai-pool is not AI 权限管理. */
export function selectedNavPath(pathname: string, nodes: NavNode[]): string {
  const links = collectLinks(nodes);
  const sorted = [...links].sort((a, b) => b.path.length - a.path.length);
  for (const link of sorted) {
    if (pathMatches(pathname, link.path)) return link.path;
  }
  return pathname;
}

export function submenuIdForPath(pathname: string, nodes: NavNode[]): string | undefined {
  for (const node of nodes) {
    if (node.kind !== 'submenu') continue;
    if (node.children.some((child) => pathMatches(pathname, child.path))) {
      return node.id;
    }
  }
  return undefined;
}

export function getShortcutLinks(nodes: NavNode[]): NavLink[] {
  return collectLinks(nodes).filter((link) => link.shortcut);
}

export function getAdminHomePath(nodes: NavNode[]): string | undefined {
  const submenu = nodes.find(
    (node): node is NavSubmenu => node.kind === 'submenu' && node.id === NAV_ADMIN_SUBMENU_ID,
  );
  return submenu?.children[0]?.path;
}

function linkToMenuItem(link: NavLink): NonNullable<MenuProps['items']>[number] {
  return {
    key: link.path,
    label: link.label,
    icon: createElement(link.icon),
  };
}

/**
 * Expanded: groups stay visible. Collapsed (80px): flatten groups to icons.
 * Admin submenu stays a submenu so antd can pop it out when collapsed.
 */
export function toAntdMenuItems(
  nodes: NavNode[],
  options: { collapsed?: boolean } = {},
): MenuProps['items'] {
  const collapsed = options.collapsed === true;
  const items: NonNullable<MenuProps['items']> = [];

  for (const node of nodes) {
    if (node.kind === 'link') {
      items.push(linkToMenuItem(node));
      continue;
    }

    if (node.kind === 'group') {
      if (collapsed) {
        items.push(...node.children.map(linkToMenuItem));
      } else {
        items.push({
          type: 'group',
          key: node.id,
          label: node.label,
          children: node.children.map(linkToMenuItem),
        });
      }
      continue;
    }

    items.push({
      key: node.id,
      label: node.label,
      icon: createElement(node.icon),
      children: node.children.map(linkToMenuItem),
    });
  }

  return items;
}
